import translation from 'translation';
import {
  commaSeperator,
  getDateTime,
  sentimentColors,
  setNumberFixed,
} from 'utils';

const { t } = translation;

const PERCENT_FACTOR = 100;
const PERCENT_PRECISION = 2;

const toPercentOfFraction = (fraction) =>
  setNumberFixed((fraction ?? 0) * PERCENT_FACTOR, PERCENT_PRECISION);

/**
 * The index endpoint only reports positive/neutral/negative as 0-1 shares of
 * `sample_size`, never raw counts, so the tooltip's per-post numbers are
 * derived by multiplying the two back out.
 */
const toCountOfFraction = (fraction, sampleSize) =>
  Math.round((fraction ?? 0) * (sampleSize ?? 0));

const toPercentOfCount = (count, total) =>
  total
    ? setNumberFixed((count / total) * PERCENT_FACTOR, PERCENT_PRECISION)
    : 0;

/**
 * The bars are drawn as shares, so the tooltip pairs each raw count with the
 * share it was calculated into, e.g. "68 (65.38٪)", at the same two-decimal
 * precision the gauge legend shows.
 */
const toCountWithPercentage = (count, percentage) =>
  t('statistics:netSentimentScore.tooltip.countWithPercentage', {
    count: commaSeperator(count ?? 0),
    percentage: commaSeperator(percentage),
  });

export const adaptNssIndex = (data) => {
  if (!data) return null;

  const sampleSize = data.sample_size ?? 0;

  return {
    nss: data.nss,
    sampleSize,
    previousNss: data.previous_nss,
    previousSampleSize: data.previous_sample_size ?? 0,
    distribution: {
      positive: toPercentOfFraction(data.positive),
      neutral: toPercentOfFraction(data.neutral),
      negative: toPercentOfFraction(data.negative),
      positiveCount: toCountOfFraction(data.positive, sampleSize),
      neutralCount: toCountOfFraction(data.neutral, sampleSize),
      negativeCount: toCountOfFraction(data.negative, sampleSize),
    },
  };
};

/**
 * The trend endpoint returns raw post counts per bucket while the chart draws
 * shares, so each bucket carries both: the percentage feeds the stacked bars
 * and the count is kept aside for the tooltip.
 *
 * Hourly buckets all fall on the same date, so they are labelled with the clock
 * time the way the other trend charts label their hourly axis.
 */
export const adaptNssTrend = (data, timeGap) => {
  const chartData = (data ?? []).map((item) => {
    const sampleSize = item?.sample_size ?? 0;
    const { date, time } = getDateTime(item?.time);
    const positive = toPercentOfCount(item?.positive, sampleSize);
    const neutral = toPercentOfCount(item?.neutral, sampleSize);
    const negative = toPercentOfCount(item?.negative, sampleSize);
    return {
      name: timeGap === 'hour' ? time : date,
      time: item?.time,
      nss: item?.nss ?? 0,
      sampleSize,
      positive,
      neutral,
      negative,
      positiveCount: toCountWithPercentage(item?.positive, positive),
      neutralCount: toCountWithPercentage(item?.neutral, neutral),
      negativeCount: toCountWithPercentage(item?.negative, negative),
    };
  });

  const hasSamples = chartData.some((item) => item.sampleSize > 0);

  const chartKeys = {
    barChartKeys: [
      { key: 'negative', fill: sentimentColors.negative, stackId: 'sentiment' },
      { key: 'neutral', fill: sentimentColors.neutral, stackId: 'sentiment' },
      { key: 'positive', fill: sentimentColors.positive, stackId: 'sentiment' },
    ],
    lineChartKeys: [{ key: 'nss', stroke: sentimentColors.score }],
  };

  const legends = {
    negative: {
      color: sentimentColors.negative,
      title: t('statistics:netSentimentScore.legend.negative'),
      tooltipKey: 'negativeCount',
    },
    neutral: {
      color: sentimentColors.neutral,
      title: t('statistics:netSentimentScore.legend.neutral'),
      tooltipKey: 'neutralCount',
    },
    positive: {
      color: sentimentColors.positive,
      title: t('statistics:netSentimentScore.legend.positive'),
      tooltipKey: 'positiveCount',
    },
    nss: {
      color: sentimentColors.score,
      title: t('statistics:netSentimentScore.legend.score'),
      tooltipTitle: t('statistics:netSentimentScore.tooltip.score'),
    },
  };

  /** The design lists the score first in the tooltip and last in the legend. */
  const tooltipOrder = ['nss', 'positive', 'neutral', 'negative'];

  return {
    chartData: hasSamples ? chartData : [],
    chartKeys,
    legends,
    tooltipOrder,
  };
};
